import { setAllInfluenced, updateInfluence } from './influence';
import { getCombinationsErrors } from '../validations/variations';
import { getSize } from '../iterable/getSize';

import { CHANNEL_VARIANTS, COMBINATIONS, COMBINATION_ATTRIBUTES_TO_RETAIN, INFLUENCE, INFLUENCE_ATTRIBUTES, RETAIN, RETAIN_ATTRIBUTES, VARIANTS } from '../../constants/variations';
import { AMOUNT, ATTRIBUTE_DEFAULTS, CURRENCIES, CURRENCY, ERROR, REQUIRED, SIZE_UNIT, VALUE, WEIGHT_UNIT } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

function isNull(value) {
  return value === null;
}

function isNullOrUndefined(value) {
  return value === null || value === undefined;
}

function isNullOrZero(value) {
  return value === null || Number(value) === 0;
}

function isEmptyValue({ attribute, channel, value }) {
  switch (channel) {
    case EBAY: {
      switch (attribute) {
        case ATTRIBUTE.PRICE:
        case ATTRIBUTE.QUANTITY: {
          return isNullOrZero(value);
        }

        default: {
          return isNull(value);
        }
      }
    }

    case ETSY: {
      switch (attribute) {
        case ATTRIBUTE.PRICE:
        case ATTRIBUTE.QUANTITY: {
          return isNullOrZero(value);
        }

        default: {
          return isNull(value);
        }
      }
    }

    case FAIRE: {
      switch (attribute) {
        case ATTRIBUTE.DISTANCE_UNIT: {
          return value === ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.DISTANCE_UNIT];
        }

        case ATTRIBUTE.MASS_UNIT: {
          return value === ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.MASS_UNIT];
        }

        case ATTRIBUTE.RESTOCK_DATE: {
          return isNullOrUndefined(value);
        }

        default: {
          return isNull(value);
        }
      }
    }

    case SHOPIFY: {
      switch (attribute) {
        case ATTRIBUTE.CAP:
        case ATTRIBUTE.CPI:
        case ATTRIBUTE.PRICE:
        case ATTRIBUTE.QUANTITY:
        case ATTRIBUTE.WEIGHT: {
          return isNullOrZero(value);
        }

        case ATTRIBUTE.UNIT: {
          return value === ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT];
        }

        default: {
          return isNull(value);
        }
      }
    }

    default: {
      return false;
    }
  }
}

function setDefaultValues({ channel, product, variations }) {
  function setDefaultValue(attributes) {
    return function map(source) {
      let combination = source;

      for (const attribute in attributes) {
        if (attributes.hasOwnProperty(attribute)) {
          switch (channel) {
            case FAIRE: {
              switch (attribute) {
                case ATTRIBUTE.RETAIL_PRICE:
                case ATTRIBUTE.WHOLESALE_PRICE: {
                  for (const currency of CURRENCIES[FAIRE]) {
                    if (isEmptyValue({ attribute, channel, value: combination.getIn([attribute, currency]) })) {
                      combination = combination.setIn([attribute, currency], attributes[attribute]);
                    }
                  }

                  continue;
                }

                default: {
                  break;
                }
              }

              break;
            }

            default: {
              break;
            }
          }

          if (isEmptyValue({ attribute, channel, value: combination.get(attribute) })) {
            combination = combination.set(attribute, attributes[attribute]);
          }
        }
      }

      return combination;
    };
  }

  switch (channel) {
    case EBAY: {
      const attributes = {
        [ATTRIBUTE.PRICE]: ATTRIBUTE_DEFAULTS[EBAY][ATTRIBUTE.PRICE],
        [ATTRIBUTE.QUANTITY]: ATTRIBUTE_DEFAULTS[EBAY][ATTRIBUTE.QUANTITY],
        [ATTRIBUTE.SKU]: ATTRIBUTE_DEFAULTS[EBAY][ATTRIBUTE.SKU],
      };

      return variations.set(COMBINATIONS, variations.get(COMBINATIONS).map(setDefaultValue(attributes)));
    }

    case ETSY: {
      const attributes = {
        [ATTRIBUTE.PRICE]: product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]),
        [ATTRIBUTE.PROCESSING_PROFILE]: product.getIn([FORM.SHIPPING, ATTRIBUTE.PROCESSING_PROFILE, VALUE]),
        [ATTRIBUTE.QUANTITY]: product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]),
        [ATTRIBUTE.SKU]: product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]),
      };

      const combinations = variations.get(COMBINATIONS).map(setDefaultValue(attributes));
      return variations.set(COMBINATIONS, combinations);
    }

    case FAIRE: {
      const attributes = {
        [ATTRIBUTE.DEADLINE_DATE]: null,
        [ATTRIBUTE.DISTANCE_UNIT]: SIZE_UNIT.IN,
        [ATTRIBUTE.END_DATE]: null,
        [ATTRIBUTE.GTIN]: '',
        [ATTRIBUTE.HEIGHT]: '',
        [ATTRIBUTE.LENGTH]: '',
        [ATTRIBUTE.MASS_UNIT]: WEIGHT_UNIT.LB,
        [ATTRIBUTE.PHOTOS]: EMPTY.LIST,
        [ATTRIBUTE.PREORDER]: false,
        [ATTRIBUTE.QUANTITY]: '',
        [ATTRIBUTE.RESTOCK_DATE]: null,
        [ATTRIBUTE.RETAIL_PRICE]: '',
        [ATTRIBUTE.SKU]: '',
        [ATTRIBUTE.START_DATE]: null,
        [ATTRIBUTE.TARIFF_CODE]: '',
        [ATTRIBUTE.WHOLESALE_PRICE]: '',
        [ATTRIBUTE.WEIGHT]: '',
        [ATTRIBUTE.WIDTH]: '',
      };

      return variations.set(COMBINATIONS, variations.get(COMBINATIONS).map(setDefaultValue(attributes)));
    }

    case SHOPIFY: {
      const attributes = {
        [ATTRIBUTE.BARCODE]: '',
        [ATTRIBUTE.CAP]: AMOUNT.ZERO_CENT,
        [ATTRIBUTE.CHARGE_TAX]: true,
        [ATTRIBUTE.CONTINUE_SELLING]: true,
        [ATTRIBUTE.COUNTRY_CODE]: null,
        [ATTRIBUTE.CPI]: AMOUNT.ZERO_CENT,
        [ATTRIBUTE.HS_CODE]: null,
        [ATTRIBUTE.PHYSICAL]: false,
        [ATTRIBUTE.PRICE]: AMOUNT.ZERO_CENT,
        [ATTRIBUTE.QUANTITY]: AMOUNT.ZERO,
        [ATTRIBUTE.SKU]: '',
        [ATTRIBUTE.TRACK_QUANTITY]: false,
        [ATTRIBUTE.UNIT]: WEIGHT_UNIT.LB,
        [ATTRIBUTE.WEIGHT]: AMOUNT.ZERO,
      };

      return variations.set(COMBINATIONS, variations.get(COMBINATIONS).map(setDefaultValue(attributes)));
    }

    default: {
      return variations;
    }
  }
}

function areVariantsSame({ channel, product, variations }) {
  function reduceChannelVariants(result, variantIndex) {
    if (!result) return result;

    const profileVariant = variations.getIn([VARIANTS, variantIndex]);
    const productVariant = product.getIn([FORM.VARIATIONS, VARIANTS, variantIndex]);

    return (
      !profileVariant ||
      !productVariant ||
      profileVariant.get('name') === productVariant.get('name')
    );
  }

  return (
    getSize(variations.get(VARIANTS)) > 0 &&
    getSize(product.getIn([FORM.VARIATIONS, VARIANTS])) > 0 &&
    CHANNEL_VARIANTS[channel].reduce(reduceChannelVariants, true)
  );
}

function retainEbayVariations({ product, variations }) {
  const channel = EBAY;

  if (!areVariantsSame({ channel, product, variations })) {
    return setDefaultValues({ channel, product, variations });
  }

  function retainPhotos({ source, target }) {
    function reduceOptions(result, option, optionIndex) {
      const value = option.get('value');

      function findSameOption(item) {
        return item.get('value') === value;
      }

      const sameOptionIndex = result.source.findIndex(findSameOption);

      if (sameOptionIndex !== -1) {
        const photos = result.source.getIn([sameOptionIndex, 'photos'], EMPTY.LIST);

        if (getSize(photos) > 0) {
          result.target = result.target.setIn([optionIndex, 'photos'], photos);
        }

        result.source = result.source.splice(sameOptionIndex, 1);
      }

      return result;
    }

    return target.reduce(reduceOptions, { source, target }).target;
  }

  function mapVariants(variant, variantIndex) {
    const productVariant = product.getIn([FORM.VARIATIONS, VARIANTS, variantIndex]);

    if (productVariant) {
      return variant.set('options',
        retainPhotos({ source: productVariant.get('options'), target: variant.get('options') })
      );
    }

    return variant;
  }

  function mapCombinations(combination, combinationIndex) {
    function shouldRetainValue(attribute) {
      return isEmptyValue({ attribute, channel, value: combination.get(attribute) });
    }

    function reduceAttributes(result, attribute) {
      if (!shouldRetainValue(attribute)) return result;

      function isMatching(productOffering) {
        function reduceOptions(match, option, optionIndex) {
          return match && option.get('name') === combination.getIn(['options', optionIndex, 'name']);
        }

        return productOffering.get('options').reduce(reduceOptions, true);
      }

      const sameIndexCombination = product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]);
      const productCombination = sameIndexCombination && isMatching(sameIndexCombination)
        ? sameIndexCombination
        : product.getIn([FORM.VARIATIONS, COMBINATIONS]).find(isMatching);

      const combinationValue = productCombination
        ? productCombination.get(attribute)
        : attribute in ATTRIBUTE_DEFAULTS[channel]
          ? ATTRIBUTE_DEFAULTS[channel][attribute]
          : null;

      return result.set(attribute, combinationValue);
    }

    return COMBINATION_ATTRIBUTES_TO_RETAIN[channel].reduce(reduceAttributes, combination);
  }

  return variations
    .set(VARIANTS, variations.get(VARIANTS).map(mapVariants))
    .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations));
}

function retainEtsyVariations({ product, variations }) {
  const channel = ETSY;

  if (!areVariantsSame({ channel, product, variations })) {
    return setDefaultValues({ channel, product, variations });
  }

  function reduceInfluence(result, attribute) {
    if (
      getSize(result.influence.get(attribute)) === 0 &&
      getSize(product.getIn([FORM.VARIATIONS, INFLUENCE, attribute])) > 0
    ) {
      result.attributesToRetain = result.attributesToRetain.add(attribute);
      result.influence = result.influence.set(attribute, product.getIn([FORM.VARIATIONS, INFLUENCE, attribute]));
    }

    return result;
  }

  const { attributesToRetain, influence } = INFLUENCE_ATTRIBUTES[channel].reduce(
    reduceInfluence,
    { attributesToRetain: EMPTY.SET, influence: variations.get(INFLUENCE) }
  );

  function retainPhotos({ source, target }) {
    function reduceOptions(result, option, optionIndex) {
      const value = option.get('value');

      function findSameOption(item) {
        return item.get('value') === value;
      }

      const sameOptionIndex = result.source.findIndex(findSameOption);

      if (sameOptionIndex !== -1) {
        const hash = result.source.getIn([sameOptionIndex, 'imageHash'], null);
        result.target = result.target.setIn([optionIndex, 'imageHash'], hash);
        result.source = result.source.splice(sameOptionIndex, 1);
      }

      return result;
    }

    return target.reduce(reduceOptions, { source, target }).target;
  }

  function mapVariants(variant, variantIndex) {
    const productVariant = product.getIn([FORM.VARIATIONS, VARIANTS, variantIndex]);

    if (productVariant) {
      return variant.set('options',
        retainPhotos({ source: productVariant.get('options'), target: variant.get('options') })
      );
    }

    return variant;
  }

  function mapCombinations(combination, combinationIndex) {
    function reduceAttributes(result, attribute) {
      function isMatching(productCombination) {
        function reduceOptions(match, option, optionIndex) {
          return match && (
            !product.getIn([FORM.VARIATIONS, INFLUENCE, attribute, optionIndex]) ||
            option.get('name') === combination.getIn(['options', optionIndex, 'name'])
          );
        }

        return productCombination.get('options').reduce(reduceOptions, true);
      }

      const sameIndexCombination = product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]);
      const productCombination = sameIndexCombination && isMatching(sameIndexCombination)
        ? sameIndexCombination
        : product.getIn([FORM.VARIATIONS, COMBINATIONS]).find(isMatching);

      const combinationValue = productCombination
        ? productCombination.get(attribute)
        : product.getIn([FORM.INVENTORY, attribute, VALUE]);

      return result.set(attribute, combinationValue);
    }

    return attributesToRetain.reduce(reduceAttributes, combination);
  }

  const variants = variations.get(VARIANTS).map(mapVariants);

  const combinations = getSize(attributesToRetain)
    ? variations.get(COMBINATIONS).map(mapCombinations)
    : variations.get(COMBINATIONS);

  return setAllInfluenced(
    variations
      .set(INFLUENCE, influence)
      .set(COMBINATIONS, combinations)
      .set(VARIANTS, variants)
  );
}

function retainFaireVariations({ product, variations }) {
  const channel = FAIRE;

  if (!areVariantsSame({ channel, product, variations })) {
    return setDefaultValues({ channel, product, variations });
  }

  function mapCombinations(combination, combinationIndex) {
    function shouldRetainValue({ attribute, currency }) {
      switch (attribute) {
        case 'imageUrl': {
          return true;
        }

        case ATTRIBUTE.DEADLINE_DATE: {
          return (
            variations.getIn([INFLUENCE, ATTRIBUTE.KEEP_ACTIVE]) ||
            isEmptyValue({
              attribute: ATTRIBUTE.DEADLINE_DATE,
              channel,
              value: combination.get(ATTRIBUTE.DEADLINE_DATE),
            })
          );
        }

        case ATTRIBUTE.PREORDER: {
          return (
            !combination.get(ATTRIBUTE.PREORDER) || (
              isEmptyValue({
                attribute: ATTRIBUTE.START_DATE,
                channel,
                value: combination.get(ATTRIBUTE.START_DATE),
              }) &&
              isEmptyValue({
                attribute: ATTRIBUTE.END_DATE,
                channel,
                value: combination.get(ATTRIBUTE.END_DATE),
              }) && (
                variations.getIn([INFLUENCE, ATTRIBUTE.KEEP_ACTIVE]) ||
                isEmptyValue({
                  attribute: ATTRIBUTE.DEADLINE_DATE,
                  channel,
                  value: combination.get(ATTRIBUTE.DEADLINE_DATE),
                })
              )
            )
          );
        }

        case ATTRIBUTE.RETAIL_PRICE: {
          switch (currency) {
            case CURRENCY.USD: {
              return isEmptyValue({
                attribute: ATTRIBUTE.RETAIL_PRICE,
                channel,
                value: combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.USD]),
              });
            }

            default: {
              return (
                !variations.getIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING]) ||
                isEmptyValue({
                  attribute: ATTRIBUTE.RETAIL_PRICE,
                  channel,
                  value: combination.getIn([ATTRIBUTE.RETAIL_PRICE, currency]),
                })
              );
            }
          }
        }

        case ATTRIBUTE.WHOLESALE_PRICE: {
          switch (currency) {
            case CURRENCY.USD: {
              return isEmptyValue({
                attribute: ATTRIBUTE.WHOLESALE_PRICE,
                channel,
                value: combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.USD]),
              });
            }

            default: {
              return (
                !variations.getIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING]) ||
                isEmptyValue({
                  attribute: ATTRIBUTE.WHOLESALE_PRICE,
                  channel,
                  value: combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, currency]),
                })
              );
            }
          }
        }

        default: {
          return isEmptyValue({ attribute, channel, value: combination.get(attribute) });
        }
      }
    }

    function reduceAttributes(result, attribute) {
      function isMatching(productOffering) {
        function reduceOptions(match, option, optionIndex) {
          return match && option.get('name') === combination.getIn(['options', optionIndex, 'name']);
        }

        return productOffering.get('options').reduce(reduceOptions, true);
      }

      function getProductCombination() {
        const sameIndexCombination = product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]);
        const productCombination = sameIndexCombination && isMatching(sameIndexCombination)
          ? sameIndexCombination
          : product.getIn([FORM.VARIATIONS, COMBINATIONS]).find(isMatching);

        return productCombination;
      }

      switch (attribute) {
        case ATTRIBUTE.RETAIL_PRICE:
        case ATTRIBUTE.WHOLESALE_PRICE: {
          const productCombination = getProductCombination();

          return CURRENCIES[FAIRE].reduce(
            function reduceCurrencies(reducedCombinaiton, currency) {
              if (!shouldRetainValue({ attribute, currency })) return result;

              const combinationValue = productCombination
                ? productCombination.getIn([attribute, currency])
                : attribute in ATTRIBUTE_DEFAULTS[channel]
                  ? ATTRIBUTE_DEFAULTS[channel][attribute]
                  : null;

              return reducedCombinaiton.setIn([attribute, currency], combinationValue);
            },
            result,
          );
        }

        default: {
          if (!shouldRetainValue({ attribute })) return result;

          const productCombination = getProductCombination();

          const combinationValue = productCombination
            ? productCombination.get(attribute)
            : attribute in ATTRIBUTE_DEFAULTS[channel]
              ? ATTRIBUTE_DEFAULTS[channel][attribute]
              : null;

          return result.set(attribute, combinationValue);
        }
      }
    }

    return COMBINATION_ATTRIBUTES_TO_RETAIN[channel].reduce(reduceAttributes, combination);
  }

  return updateInfluence({
    channel,
    inventory: product.get(FORM.INVENTORY),
    variations: variations.set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations)),
  });
}

function retainShopifyVariations({ product, variations }) {
  const channel = SHOPIFY;

  if (!areVariantsSame({ channel, product, variations })) {
    return setDefaultValues({ channel, product, variations });
  }

  function mapCombinations(combination, combinationIndex) {
    function shouldRetainValue(attribute) {
      switch (attribute) {
        case 'imageId':
        case 'imageHash':
        case ATTRIBUTE.CHARGE_TAX:
        case ATTRIBUTE.CONTINUE_SELLING: {
          return true;
        }

        case ATTRIBUTE.PHYSICAL: {
          return (
            !combination.get(ATTRIBUTE.PHYSICAL) || (
              isEmptyValue({
                attribute: ATTRIBUTE.COUNTRY_CODE,
                channel,
                value: combination.get(ATTRIBUTE.COUNTRY_CODE),
              }) &&
              isEmptyValue({ attribute: ATTRIBUTE.HS_CODE, channel, value: combination.get(ATTRIBUTE.HS_CODE) }) &&
              isEmptyValue({ attribute: ATTRIBUTE.WEIGHT, channel, value: combination.get(ATTRIBUTE.WEIGHT) }) &&
              isEmptyValue({ attribute: ATTRIBUTE.UNIT, channel, value: combination.get(ATTRIBUTE.UNIT) })
            )
          );
        }

        case ATTRIBUTE.TRACK_QUANTITY: {
          return isEmptyValue({ attribute: ATTRIBUTE.QUANTITY, channel, value: combination.get(ATTRIBUTE.QUANTITY) });
        }

        default: {
          return isEmptyValue({ attribute, channel, value: combination.get(attribute) });
        }
      }
    }

    function reduceAttributes(result, attribute) {
      if (!shouldRetainValue(attribute)) return result;

      function isMatching(productOffering) {
        function reduceOptions(match, option, optionIndex) {
          return match && option.get('name') === combination.getIn(['options', optionIndex, 'name']);
        }

        return productOffering.get('options').reduce(reduceOptions, true);
      }

      const sameIndexCombination = product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]);
      const productCombination = sameIndexCombination && isMatching(sameIndexCombination)
        ? sameIndexCombination
        : product.getIn([FORM.VARIATIONS, COMBINATIONS]).find(isMatching);

      const combinationValue = productCombination
        ? productCombination.get(attribute)
        : attribute in ATTRIBUTE_DEFAULTS[channel]
          ? ATTRIBUTE_DEFAULTS[channel][attribute]
          : null;

      return result.set(attribute, combinationValue);
    }

    return COMBINATION_ATTRIBUTES_TO_RETAIN[channel].reduce(reduceAttributes, combination);
  }

  return variations.set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations));
}

export function retain({ channel, product, shopData, validate = true, variations }) {
  switch (channel) {
    case EBAY: {
      const retained = retainEbayVariations({
        product,
        variations: variations.delete(RETAIN).set(REQUIRED, product.getIn([FORM.VARIATIONS, REQUIRED])),
      });

      return validate
        ? retained.set(ERROR, getCombinationsErrors({ channel, shopData, variations: retained }))
        : retained;
    }

    case ETSY: {
      const retained = retainEtsyVariations({ product, variations: variations.delete(RETAIN) });
      return validate
        ? retained.set(ERROR, getCombinationsErrors({ channel, shopData, variations: retained }))
        : retained;
    }

    case FAIRE: {
      const retained = retainFaireVariations({ product, variations: variations.delete(RETAIN) });
      return validate
        ? retained.set(ERROR, getCombinationsErrors({ channel, shopData, variations: retained }))
        : retained;
    }

    case SHOPIFY: {
      const retained = retainShopifyVariations({ product, variations: variations.delete(RETAIN) });
      return validate
        ? retained.set(ERROR, getCombinationsErrors({ channel, shopData, variations: retained }))
        : retained;
    }

    default: {
      return variations.delete(RETAIN);
    }
  }
}

export function getRetainFromVariations({ channel, variations }) {
  function canRetainEbayAttribute({ attribute, combination }) {
    return isEmptyValue({ attribute, channel, value: combination.get(attribute) });
  }

  function canRetainEtsyAttribute(attribute) {
    return getSize(variations.getIn([INFLUENCE, attribute])) === 0;
  }

  function canRetainFaireAttribute({ attribute, combination, currency }) {
    switch (attribute) {
      case ATTRIBUTE.PREORDER: {
        return (
          !combination.get(ATTRIBUTE.PREORDER) || (
            isEmptyValue({
              attribute: ATTRIBUTE.START_DATE,
              channel,
              value: combination.get(ATTRIBUTE.START_DATE),
            }) &&
            isEmptyValue({
              attribute: ATTRIBUTE.END_DATE,
              channel,
              value: combination.get(ATTRIBUTE.END_DATE),
            }) && (
              variations.getIn([INFLUENCE, ATTRIBUTE.KEEP_ACTIVE]) ||
              isEmptyValue({
                attribute: ATTRIBUTE.DEADLINE_DATE,
                channel,
                value: combination.get(ATTRIBUTE.DEADLINE_DATE),
              })
            )
          )
        );
      }

      case ATTRIBUTE.PRICE: {
        switch (currency) {
          case CURRENCY.USD: {
            return (
              isEmptyValue({
                attribute: ATTRIBUTE.WHOLESALE_PRICE,
                channel,
                value: combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, currency]),
              }) &&
              isEmptyValue({
                attribute: ATTRIBUTE.RETAIL_PRICE,
                channel,
                value: combination.getIn([ATTRIBUTE.RETAIL_PRICE, currency]),
              })
            );
          }

          default: {
            return (
              !variations.getIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING]) || (
                isEmptyValue({
                  attribute: ATTRIBUTE.WHOLESALE_PRICE,
                  channel,
                  value: combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, currency]),
                }) &&
                isEmptyValue({
                  attribute: ATTRIBUTE.RETAIL_PRICE,
                  channel,
                  value: combination.getIn([ATTRIBUTE.RETAIL_PRICE, currency]),
                })

              )
            );
          }
        }
      }

      case ATTRIBUTE.QUANTITY: {
        return (
          isEmptyValue({ attribute: ATTRIBUTE.QUANTITY, channel, value: combination.get(ATTRIBUTE.QUANTITY) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.RESTOCK_DATE, channel, value: combination.get(ATTRIBUTE.RESTOCK_DATE) })
        );
      }

      case ATTRIBUTE.SHIPPING: {
        return (
          isEmptyValue({ attribute: ATTRIBUTE.TARIFF_CODE, channel, value: combination.get(ATTRIBUTE.TARIFF_CODE) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.HEIGHT, channel, value: combination.get(ATTRIBUTE.HEIGHT) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.LENGTH, channel, value: combination.get(ATTRIBUTE.LENGTH) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.WEIGHT, channel, value: combination.get(ATTRIBUTE.WEIGHT) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.WIDTH, channel, value: combination.get(ATTRIBUTE.WIDTH) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.MASS_UNIT, channel, value: combination.get(ATTRIBUTE.MASS_UNIT) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.DISTANCE_UNIT, channel, value: combination.get(ATTRIBUTE.DISTANCE_UNIT) })
        );
      }

      default: {
        return isEmptyValue({ attribute, channel, value: combination.get(attribute) });
      }
    };
  }

  function canRetainShopifyAttribute({ attribute, combination }) {
    switch (attribute) {
      case ATTRIBUTE.PRICE: {
        return (
          isEmptyValue({ attribute: ATTRIBUTE.CAP, channel, value: combination.get(ATTRIBUTE.CAP) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.CPI, channel, value: combination.get(ATTRIBUTE.CPI) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.PRICE, channel, value: combination.get(ATTRIBUTE.PRICE) })
        );
      }

      case ATTRIBUTE.QUANTITY: {
        return (
          !combination.get(ATTRIBUTE.TRACK_QUANTITY) ||
          isEmptyValue({ attribute: ATTRIBUTE.QUANTITY, channel, value: combination.get(ATTRIBUTE.QUANTITY) })
        );
      }

      case ATTRIBUTE.SHIPPING: {
        return (
          !combination.get(ATTRIBUTE.PHYSICAL) || (
            isEmptyValue({
              attribute: ATTRIBUTE.COUNTRY_CODE,
              channel,
              value: combination.get(ATTRIBUTE.COUNTRY_CODE),
            }) &&
            isEmptyValue({ attribute: ATTRIBUTE.HS_CODE, channel, value: combination.get(ATTRIBUTE.HS_CODE) }) &&
            isEmptyValue({ attribute: ATTRIBUTE.WEIGHT, channel, value: combination.get(ATTRIBUTE.WEIGHT) }) &&
            isEmptyValue({ attribute: ATTRIBUTE.UNIT, channel, value: combination.get(ATTRIBUTE.UNIT) })
          )
        );
      }

      default: {
        return isEmptyValue({ attribute, channel, value: combination.get(attribute) });
      }
    }
  }

  function reduceEbayAttributes(result, attribute) {
    if (result.retain.get(attribute) !== false) {
      result.retain = result.retain.set(attribute,
        canRetainEbayAttribute({ attribute, combination: result.combination })
      );
    }

    return result;
  }

  function reduceEtsyAttributes(result, attribute) {
    return result.get(attribute) === false
      ? result
      : result.set(attribute, canRetainEtsyAttribute(attribute));
  }

  function reduceFaireAttributes(result, attribute) {
    if (result.retain.get(attribute) !== false) {
      switch (attribute) {
        case ATTRIBUTE.PRICE: {
          result.retain = result.retain.set(attribute,
            CURRENCIES[FAIRE].reduce(
              function reduceCurrencies(retainPrice, currency) {
                return retainPrice && canRetainFaireAttribute({ attribute, combination: result.combination, currency });
              },
              true
            )
          );

          break;
        }

        default: {
          result.retain = result.retain.set(attribute,
            canRetainFaireAttribute({ attribute, combination: result.combination })
          );
          break;
        }
      }
    }

    return result;
  }

  function reduceShopifyAttributes(result, attribute) {
    if (result.retain.get(attribute) !== false) {
      result.retain = result.retain.set(attribute,
        canRetainShopifyAttribute({ attribute, combination: result.combination })
      );
    }

    return result;
  }

  function reduceEbayCombinations(result, combination) {
    return RETAIN_ATTRIBUTES[EBAY].reduce(reduceEbayAttributes, { combination, retain: result }).retain;
  }

  function reduceFaireCombinations(result, combination) {
    return RETAIN_ATTRIBUTES[FAIRE].reduce(reduceFaireAttributes, { combination, retain: result }).retain;
  }

  function reduceShopifyCombinations(result, combination) {
    return RETAIN_ATTRIBUTES[SHOPIFY].reduce(reduceShopifyAttributes, { combination, retain: result }).retain;
  }

  switch (channel) {
    case EBAY: {
      if (getSize(variations.get(COMBINATIONS)) === 0) return EMPTY.MAP;

      return variations.get(COMBINATIONS).reduce(reduceEbayCombinations, EMPTY.MAP);
    }

    case ETSY: {
      if (getSize(variations.get(COMBINATIONS)) === 0) return EMPTY.MAP;

      return RETAIN_ATTRIBUTES[ETSY].reduce(reduceEtsyAttributes, EMPTY.MAP);
    }

    case FAIRE: {
      if (getSize(variations.get(COMBINATIONS)) === 0) return EMPTY.MAP;

      return variations.get(COMBINATIONS).reduce(reduceFaireCombinations, EMPTY.MAP);
    }

    case SHOPIFY: {
      if (getSize(variations.get(COMBINATIONS)) === 0) return EMPTY.MAP;

      return variations.get(COMBINATIONS).reduce(reduceShopifyCombinations, EMPTY.MAP);
    }

    default: {
      return EMPTY.MAP;
    }
  }
}
